import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ApiService from "../utils/fetch";

const BASE = "/v1/realignment/demandPlannerMapping";

// ── Thunks ───────────────────────────────────────────────────────────────────

export const fetchProfitCenterUsers = createAsyncThunk(
  "profitCenterConfiguration/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiService.get(`${BASE}/users`);
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchProfitCenterOptions = createAsyncThunk(
  "profitCenterConfiguration/fetchProfitCenterOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiService.get(`${BASE}/countries`);
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchUserMappings = createAsyncThunk(
  "profitCenterConfiguration/fetchUserMappings",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await ApiService.get(`${BASE}/mappings/${userId}`);
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const saveUserMappings = createAsyncThunk(
  "profitCenterConfiguration/saveUserMappings",
  async ({ userId, mappings }, { rejectWithValue }) => {
    try {
      console.log("Saving user mappings:", { userId, mappings });
      const response = await ApiService.post(`${BASE}/mappings`, { userId, mappings });
      if (response.data.success) {
        return {
          updatedMappings: response.data.data,
          message: response.data.message,
        };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const toggleUserMapping = createAsyncThunk(
  "profitCenterConfiguration/toggleUserMapping",
  async ({ id, isEnabled }, { rejectWithValue }) => {
    try {
      const response = await ApiService.put(
        `${BASE}/mappings/${id}/toggle?isEnabled=${isEnabled}`,
      );
      if (response.data.success) {
        return {
          updatedMapping: response.data.data,
          message: response.data.message,
        };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const bulkToggleUserMappings = createAsyncThunk(
  "profitCenterConfiguration/bulkToggleUserMappings",
  async ({ mappingIds, isEnabled }, { rejectWithValue }) => {
    try {
      const response = await ApiService.put(`${BASE}/mappings/toggle/bulk`, {
        mappingIds,
        isEnabled,
      });
      if (response.data.success) {
        return {
          updatedMappings: response.data.data,
          message: response.data.message,
        };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
  users: [],
  profitCenterOptions: [],
  /** All mappings (enabled + disabled) for the currently selected user. */
  userMappings: [],
  loading: false,
  loadingMappings: false,
  saving: false,
  error: null,
  successMessage: null,
};

const profitCenterConfigurationSlice = createSlice({
  name: "profitCenterConfiguration",
  initialState,
  reducers: {
    clearProfitCenterMessage: (state) => {
      state.successMessage = null;
      state.error = null;
    },
    clearUserMappings: (state) => {
      state.userMappings = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchProfitCenterUsers
      .addCase(fetchProfitCenterUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfitCenterUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchProfitCenterUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchProfitCenterOptions
      .addCase(fetchProfitCenterOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfitCenterOptions.fulfilled, (state, action) => {
        state.loading = false;
        state.profitCenterOptions = action.payload;
      })
      .addCase(fetchProfitCenterOptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchUserMappings
      .addCase(fetchUserMappings.pending, (state) => {
        state.loadingMappings = true;
        state.error = null;
      })
      .addCase(fetchUserMappings.fulfilled, (state, action) => {
        state.loadingMappings = false;
        state.userMappings = action.payload;
      })
      .addCase(fetchUserMappings.rejected, (state, action) => {
        state.loadingMappings = false;
        state.error = action.payload;
      })

      // saveUserMappings
      .addCase(saveUserMappings.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveUserMappings.fulfilled, (state, action) => {
        state.saving = false;
        state.userMappings = [...state.userMappings, ...action.payload.updatedMappings];
        state.successMessage = action.payload.message;
      })
      .addCase(saveUserMappings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // toggleUserMapping
      .addCase(toggleUserMapping.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(toggleUserMapping.fulfilled, (state, action) => {
        state.saving = false;
        const updated = action.payload.updatedMapping;
        state.userMappings = state.userMappings.map((m) =>
          m.id === updated.id ? updated : m,
        );
        state.successMessage = action.payload.message;
      })
      .addCase(toggleUserMapping.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // bulkToggleUserMappings
      .addCase(bulkToggleUserMappings.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(bulkToggleUserMappings.fulfilled, (state, action) => {
        state.saving = false;
        const updatedMap = new Map(
          action.payload.updatedMappings.map((m) => [m.id, m]),
        );
        state.userMappings = state.userMappings.map((m) =>
          updatedMap.has(m.id) ? updatedMap.get(m.id) : m,
        );
        state.successMessage = action.payload.message;
      })
      .addCase(bulkToggleUserMappings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { clearProfitCenterMessage, clearUserMappings } =
  profitCenterConfigurationSlice.actions;
export default profitCenterConfigurationSlice.reducer;
